import React, {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {StackProps, useMultiStyleConfig, VStack, TextProps, Text, BoxProps, HStack, Flex, FlexProps, Box} from '@chakra-ui/react'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Navigation} from 'swiper/modules'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {Icon, ProductTile} from '_uiComponents'

interface DoubleSameItemProps {
    productsIdList: Array<string>
    title?: string
    text?: string
    analyticsList?: string
    id: string
}

/**
 * @name DoubleSameItem
 *
 * @component
 *
 * @description
 * Renders a container for two possible items displayed in row.
 *
 * @param {DoubleSameItemProps} props - Properties object.
 * @param {Array<string>} props.productsIdList - List of product IDs.
 * @param {string} [props.title] - The title for the element
 * @param {string} [props.text] - The description for the element.
 *
 * @returns {JSX.Element} The rendered `DoubleSameItem` component.
 */
const DoubleSameItem = ({productsIdList, title, text, analyticsList, id}: DoubleSameItemProps): JSX.Element => {
    const {formatMessage} = useIntl()
    const analyticsListRelatives = analyticsList ? analyticsList.replace('editorial', 'relatives-editorial') : ''
    const [activeIndex, setActiveIndex] = useState(0)
    const [containerVisible, setContainerVisible] = useState(false)
    const viewedSlides = useRef(new Set())
    const containerRef = useRef(null)
    const {data: products} = useProducts({
        parameters: {
            ids: productsIdList?.join(','),
            allImages: true,
            expand: ['none'],
        },
    })

    const hasProductsCarousel = !!(products && products?.data?.length)
    const styles = useMultiStyleConfig('DoubleSameItem', {hasProductsCarousel}) as Record<string, StackProps & TextProps & BoxProps & FlexProps>

    useEffect(() => {
        if (!containerRef.current) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                setContainerVisible(entry.isIntersecting)
            },
            {
                root: null,
                threshold: 0.5, // almeno 50% visibile
            },
        )

        observer.observe(containerRef.current)

        return () => {
            observer.disconnect()
        }
    }, [])

    // Gestione cambio slide
    const handleSlideChange = (swiper) => {
        setActiveIndex(swiper.realIndex)
    }

    return (
        <VStack {...styles.wrapper} data-testid="double-same-item" className="cc-slider-double-same-container" ref={containerRef}>
            <Box className="container" {...styles.containerItem}>
                {(title || text) && (
                    <VStack {...styles.body} className="cc-slide-info-container">
                        {title && (
                            <Text as="h1" {...styles.title} className="cc-slide-title">
                                {title}
                            </Text>
                        )}
                        {text && (
                            <Text as="p" {...styles.text} className="cc-slide-desc">
                                {text}
                            </Text>
                        )}
                    </VStack>
                )}
                {hasProductsCarousel && (
                    <HStack sx={styles.container} data-testid={'swiper-double-same'} className="cc-content-double-same-slider">
                        {products?.data?.length > 1 && (
                            <Flex
                                as="button"
                                {...styles.navigationPrev}
                                aria-label={formatMessage({id: 'accessibility.button.slide.previous.products', defaultMessage: 'Previous products'})}
                                className={`cc-slider-prev swiper-button-prev swiper-button-prev-double-same${id}`}
                                data-testid={'swiper-button-prev-double-same' + id}
                            >
                                <Icon name="IconChevronLeft" size="sm" className="cc-svg cc-svg-slider-prev" />
                            </Flex>
                        )}
                        <Swiper
                            className="cc-slider"
                            modules={[Navigation]}
                            navigation={{
                                prevEl: `.swiper-button-prev-double-same${id}`,
                                nextEl: `.swiper-button-next-double-same${id}`,
                            }}
                            slidesPerView={1}
                            updateOnWindowResize={true}
                            loop={true}
                            onAfterInit={() => {
                                const bodyElements = document.querySelectorAll('.cc-slide-info-container')
                                if (bodyElements.length > 0) {
                                    bodyElements.forEach((bodyElement) => {
                                        ;(bodyElement as HTMLElement).style.display = 'flex'
                                    })
                                }
                            }}
                            onSlideChange={handleSlideChange}
                            style={products?.data?.length > 1 ? undefined : {width: '100%'}}
                        >
                            {products.data.map((product, index) => {
                                const shouldTriggerImpression = containerVisible && index === activeIndex && !viewedSlides.current.has(index)

                                if (shouldTriggerImpression) {
                                    viewedSlides.current.add(index)
                                }

                                return (
                                    <SwiperSlide key={'swiper-slide-' + id + '-' + String(index)} className="cc-pd-doublesame-item">
                                        <ProductTile
                                            product={product}
                                            shouldHideWishlistButton={true}
                                            analyticsList={analyticsListRelatives}
                                            idx={index}
                                            triggerImpression={shouldTriggerImpression}
                                            showCarousel={false}
                                            showCover={false}
                                        />
                                    </SwiperSlide>
                                )
                            })}
                        </Swiper>
                        {products?.data?.length > 1 && (
                            <Flex
                                as="button"
                                {...styles.navigationNext}
                                aria-label={formatMessage({id: 'accessibility.button.slide.next.products', defaultMessage: 'Next products'})}
                                className={`cc-slider-next swiper-button-next swiper-button-next-double-same${id}`}
                                data-testid={'swiper-button-next-double-same' + id}
                            >
                                <Icon name="IconChevronRight" size="sm" className="cc-svg cc-svg-slider-next" />
                            </Flex>
                        )}
                    </HStack>
                )}
            </Box>
        </VStack>
    )
}

export {DoubleSameItem}
